import {
  BerthDto,
  CarrierVisitDto,
  ContainerTurnoverDto,
  GeneralCargoOrderPlanningDto,
  GeneralCargoStockDto,
  ReloadStacksNotification,
  StowageInstructionResponseDto,
  YardBlockDto,
} from '@storage/app/api'
import { HubContext } from '@tom-ui/utils'

const METHODS = {
  BerthCreated: 'BerthCreated',
  BerthUpdated: 'BerthUpdated',
  BerthDeleted: 'BerthDeleted',

  YardBlockCreated: 'YardBlockCreated',
  YardBlockUpdated: 'YardBlockUpdated',
  YardBlockDeleted: 'YardBlockDeleted',

  ReloadStacks: 'ReloadStacks',
  ReloadYardBlock: 'ReloadYardBlock',
  ReloadRules: 'ReloadRules',

  CarrierVisitUpserted: 'CarrierVisitUpserted',
  CarrierVisitsDeleted: 'CarrierVisitsDeleted',

  OrdersUpserted: 'OrdersUpserted',
  TurnoverCharacteristicsChanged: 'TurnoverCharacteristicsChanged',
  TurnoverPositionChanged: 'TurnoverPositionChanged',

  GeneralCargoStockUpserted: 'GeneralCargoStockUpserted',
  GeneralCargoStockDeleted: 'GeneralCargoStockDeleted',
  GeneralCargoOrderPlanningUpserted: 'GeneralCargoOrderPlanningUpserted',

  StowageInstructionUpserted: 'StowageInstructionUpserted',
  StowageInstructionDeleted: 'StowageInstructionDeleted',
}

export interface YardManagementHubConnection extends HubContext {
  onBerthCreated: (action: (berth: BerthDto) => void) => void
  onBerthUpdated: (action: (berth: BerthDto) => void) => void
  onBerthDeleted: (action: (berthId: string) => void) => void

  onYardBlockCreated: (action: (yardBlock: YardBlockDto) => void) => void
  onYardBlockUpdated: (action: (yardBlockId: string) => void) => void
  onYardBlockDeleted: (action: (yardBlockId: string) => void) => void

  onReloadStacks: (action: (notification: ReloadStacksNotification) => void) => void
  onReloadYardBlock: (action: (yardBlockId: string) => void) => void
  onReloadRules: (action: () => void) => void

  onCarrierVisitUpserted: (action: (carrierVisit: CarrierVisitDto) => void) => void
  onCarrierVisitsDeleted: (action: (carrierVisitIds: number[]) => void) => void

  onOrdersUpserted: (action: (containerTurnovers: ContainerTurnoverDto[]) => void) => void

  onGeneralCargoStockUpserted: (action: (stock: GeneralCargoStockDto) => void) => void
  onGeneralCargoStockDeleted: (action: (stockId: string) => void) => void
  onGeneralCargoOrderPlanningUpserted: (
    action: (planning: GeneralCargoOrderPlanningDto) => void,
  ) => void
  offGeneralCargoOrderPlanningUpserted: (
    action: (planning: GeneralCargoOrderPlanningDto) => void,
  ) => void

  onTurnoverCharacteristicsChanged: (
    action: (containerTurnover: ContainerTurnoverDto) => void,
  ) => void

  onStowageInstructionUpserted: (
    action: (stowageInstruction: StowageInstructionResponseDto) => void,
  ) => void

  onStowageInstructionDeleted: (action: (stowageInstructionIds: string[]) => void) => void
}

export class YardManagementHubConnectionImplementation
  extends HubContext
  implements YardManagementHubConnection
{
  constructor() {
    super('/storage/hubs/yard-management-hub')
  }
  public onBerthCreated(action: (berthDto: BerthDto) => void) {
    this.connection.on(METHODS.BerthCreated, action)
  }

  public onBerthUpdated(action: (berth: BerthDto) => void) {
    this.connection.on(METHODS.BerthUpdated, action)
  }

  public onBerthDeleted(action: (berthId: string) => void) {
    this.connection.on(METHODS.BerthDeleted, action)
  }

  public onYardBlockCreated(action: (yardBlock: YardBlockDto) => void) {
    this.connection.on(METHODS.YardBlockCreated, action)
  }

  public onYardBlockUpdated(action: (yardBlockId: string) => void) {
    this.connection.on(METHODS.YardBlockUpdated, action)
  }

  public onYardBlockDeleted(action: (yardBlockId: string) => void) {
    this.connection.on(METHODS.YardBlockDeleted, action)
  }

  public onReloadStacks(action: (notification: ReloadStacksNotification) => void) {
    this.connection.on(METHODS.ReloadStacks, action)
  }

  public onReloadRules(action: () => void) {
    this.connection.on(METHODS.ReloadRules, action)
  }

  public onCarrierVisitUpserted(action: (carrierVisit: CarrierVisitDto) => void) {
    this.connection.on(METHODS.CarrierVisitUpserted, action)
  }

  public onCarrierVisitsDeleted(action: (carrierVisitIds: number[]) => void) {
    this.connection.on(METHODS.CarrierVisitsDeleted, action)
  }

  public onReloadYardBlock(action: (yardBlockId: string) => void) {
    this.connection.on(METHODS.ReloadYardBlock, action)
  }

  public onOrdersUpserted(action: (containerTurnovers: ContainerTurnoverDto[]) => void) {
    this.connection.on(METHODS.OrdersUpserted, action)
  }

  public onTurnoverCharacteristicsChanged(
    action: (containerTurnover: ContainerTurnoverDto) => void,
  ) {
    this.connection.on(METHODS.TurnoverCharacteristicsChanged, action)
  }

  public onGeneralCargoStockUpserted(action: (stock: GeneralCargoStockDto) => void) {
    this.connection.on(METHODS.GeneralCargoStockUpserted, action)
  }

  public onGeneralCargoStockDeleted(action: (stockId: string) => void) {
    this.connection.on(METHODS.GeneralCargoStockDeleted, action)
  }

  public onGeneralCargoOrderPlanningUpserted(
    action: (planning: GeneralCargoOrderPlanningDto) => void,
  ) {
    this.connection.on(METHODS.GeneralCargoOrderPlanningUpserted, action)
  }

  public offGeneralCargoOrderPlanningUpserted(
    action: (planning: GeneralCargoOrderPlanningDto) => void,
  ) {
    this.connection.off(METHODS.GeneralCargoOrderPlanningUpserted, action)
  }

  public onStowageInstructionUpserted(
    action: (stowageInstruction: StowageInstructionResponseDto) => void,
  ) {
    this.connection.on(METHODS.StowageInstructionUpserted, action)
  }

  public onStowageInstructionDeleted(action: (stowageInstructionIds: string[]) => void) {
    this.connection.on(METHODS.StowageInstructionDeleted, action)
  }
}

export const yardManagementHubConnection: YardManagementHubConnection =
  new YardManagementHubConnectionImplementation()
